import { Grid, Typography, Box, debounce, Link, Chip } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import "../assets/styles/common-styles.scss";
import { DataGridComponent } from "../components/common/DataGridComponent";
import {
  GridColDef,
  GridSortModel,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid-pro";
import { useSnackbar } from "../hooks/useSnackbar";
import {
  codelist_categories,
  initialPaginationModelValues,
} from "../constants/paginationConstants";
import { useCodelist } from "../hooks/useCodelist";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../store";
import { setCodelistDetails } from "../store/slices/commonSlice";
import { usePermissionWrapper } from "../hooks/usePermissionWrapper";

export const getChipType = (category: string) => {
  switch (category) {
    case "System":
      return (
        <Chip label={category} color="info" size="small" variant="outlined" />
      );
    case "Modifiable":
      return (
        <Chip
          label={category}
          color="success"
          size="small"
          variant="outlined"
        />
      );
    case "Extensible":
      return (
        <Chip
          label={category}
          color="warning"
          size="small"
          variant="outlined"
        />
      );
  }
};

const Codelists: React.FunctionComponent<any> = () => {
  const [rowData, setRowData] = useState([]);
  const { handleError } = useSnackbar();
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState(
    initialPaginationModelValues
  );
  const { checkPermission } = usePermissionWrapper();
  const [filterObj, setFilterObject] = useState("");
  const {
    getAllCodelists,
    allCodelistData,
    allCodelistIsLoading,
    allCodelistIsError,
    allCodelistError,
  } = useCodelist();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "code_list_code", sort: "asc" },
  ]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const handleSortModelChange = useCallback(
    debounce((model: GridSortModel) => {
      setSortModel(model ? model : [{ field: "code_list_code", sort: "asc" }]);
    }, 300),
    []
  );
  const getAllCodelistFunc = (from: number, size: number, filterObj?: any) => {
    getAllCodelists({
      from,
      size,
      value: filterObj && filterObj?.quickFilter ? filterObj?.quickFilter : "",
      sort_by: sortModel?.[0]?.field,
      sort_order: sortModel?.[0]?.sort,
      data: filterObj && filterObj?.advanceFilter,
    });
  };
  useEffect(() => {
    if (sortModel.length === 0) {
      setSortModel([{ field: "code_list_code", sort: "asc" }]);
    }
  }, [sortModel]);

  useEffect(() => {
    getAllCodelists({ is_audit: true });
  }, []);

  useEffect(() => {
    if (paginationModel?.pageSize) {
      const { page, pageSize } = paginationModel;
      if (page * pageSize > rowsCount - 1) {
        getAllCodelistFunc(0, pageSize, filterObj);
      } else {
        getAllCodelistFunc(page * pageSize, pageSize, filterObj);
      }
    }
  }, [paginationModel, filterObj]);

  useEffect(() => {
    if (allCodelistData && allCodelistData?.count > 0) {
      setRowsCount(allCodelistData?.count);

      const data = allCodelistData?.result?.map((ele: any) => {
        return {
          ...ele,
          id: ele.code_list_code,
        };
      });
      setRowData(data);
    } else if (allCodelistData?.count == 0) {
      setRowsCount(0);
      setRowData([]);
    }
  }, [allCodelistData]);

  useEffect(() => {
    if (allCodelistIsError) {
      setRowData([]);
      setRowsCount(0);
      const error: any = allCodelistError;
      handleError(error);
    }
  }, [allCodelistIsError]);

  const columns: GridColDef[] = [
    {
      field: "code_list_code",
      headerName: "Codelist Code",
      minWidth: 180,
      flex: 1,
      renderCell: (params: any) => (
        <>
          {checkPermission("VIEW_CODELIST_DETAILS") ? (
            <Link
              onClick={() => {
                navigate(`/codelists/${params.row.code_list_code}`);
                dispatch(setCodelistDetails(params.row));
              }}
            >
              {params.row.code_list_code}
            </Link>
          ) : (
            params.row.code_list_code
          )}
        </>
      ),
    },
    {
      field: "code_list_name",
      headerName: "Codelist Name",
      minWidth: 180,
      flex: 1,
    },
    {
      field: "category",
      headerName: "Category",
      width: 140,
      flex: 0.7,
      type: "singleSelect",
      valueOptions: codelist_categories,
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === "is"
      ),
      renderCell: (params: any) => getChipType(params.row.category),
    },
    {
      field: "code_list_definition",
      headerName: "Codelist Definition",
      minWidth: 200,
      flex: 2,
    },
    {
      field: "code_list_submission_value",
      headerName: "Codelist Submission Value",
      minWidth: 180,
      flex: 1.25,
    },
    {
      field: "version",
      headerName: "Version",
      minWidth: 100,
      type: "number",
    },
  ];

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs>
          <Typography variant="h4" gutterBottom>
            Codelists
          </Typography>
        </Grid>
      </Grid>
      <Box
        className="dataGridWrapBox"
        sx={{ width: "100%", height: "calc(100vh - 160px)" }}
      >
        <DataGridComponent
          columns={columns}
          rows={rowData ?? []}
          showQuickFilter
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={allCodelistIsLoading}
          rowsCount={rowsCount}
          filterMode="server"
          filterValues={(data: any) => setFilterObject(data)}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          disableRowSelectionOnClick
          exportFileName="Audit_Trail"
        />
      </Box>
    </>
  );
};

export default Codelists;
